/* eslint-disable indent */
import { useRef, useState } from 'react';
import { Crop, PixelCrop } from 'react-image-crop';

export const useCropVideo = () => {
  const initialCropConfig: Crop = {
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  };

  const [cropConfigs, setCropConfigs] = useState<Crop>(initialCropConfig);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const resetCrop = () => {
    setCropConfigs(initialCropConfig);
    setCompletedCrop(null);
  };

  const getCroppedImage = async (cropFullVideoByDefault: boolean) => {
    if (!videoRef.current) return null;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    // Ensure the canvas matches the video resolution
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

    // If cropping the full video, return the entire video frame
    if (cropFullVideoByDefault) {
      return new Promise<string>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          }
        }, 'image/jpeg');
      });
    }

    // Determine the crop to use (either user-modified or initial)
    const cropToUse = completedCrop
      ? {
          x: completedCrop.x * (videoWidth / video.clientWidth),
          y: completedCrop.y * (videoHeight / video.clientHeight),
          width: completedCrop.width * (videoWidth / video.clientWidth),
          height: completedCrop.height * (videoHeight / video.clientHeight),
        }
      : {
          x: (initialCropConfig.x / 100) * videoWidth,
          y: (initialCropConfig.y / 100) * videoHeight,
          width: (initialCropConfig.width / 100) * videoWidth,
          height: (initialCropConfig.height / 100) * videoHeight,
        };

    // Create another canvas for cropping
    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');

    if (!croppedCtx) return null;

    // Set the cropped canvas size
    croppedCanvas.width = cropToUse.width;
    croppedCanvas.height = cropToUse.height;

    // Draw the cropped area
    croppedCtx.drawImage(
      canvas,
      cropToUse.x,
      cropToUse.y,
      cropToUse.width,
      cropToUse.height,
      0,
      0,
      cropToUse.width,
      cropToUse.height,
    );

    return new Promise<string>((resolve) => {
      croppedCanvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        }
      }, 'image/jpeg');
    });
  };

  return {
    videoRef,
    cropConfigs,
    setCropConfigs,
    setCompletedCrop,
    getCroppedImage,
    resetCrop,
  };
};
